<template>

    <div class="container">

        <app-head title="Users" />

        <back />

        <div class="d-flex justify-content-start align-items-center mb-3 gap-3">
            <h1 class="">{{ __('Users') }}</h1>
            <div class="">
                <inertia-link class="btn btn-primary" :href="route('admin.users.create', {})">
                    {{ __('Create') }}
                </inertia-link>
            </div>
        </div>

        <div class="d-flex mb-3">
            <search-filter v-model="form.search" @reset="reset">
                <select-input label="account" v-model="form.account_id">
                    <!--@todo datalist or select2 ?-->
                    <option :value="null">{{ __('Alle') }}</option>
                    <option :value="account.id" v-for="account in accounts">{{ account.name }}</option>
                </select-input>
                <select-input label="role" v-model="form.role_id">
                    <option :value="null">{{ __('Alle') }}</option>
                    <option :value="role.id" v-for="role in roles">{{ role.translated_name }}</option>
                </select-input>
                <select-input label="Status" v-model="form.status">
                    <option :value="null">{{ __('Veröffentlicht') }}</option>
                    <option value="trashed">{{ __('Papierkorb') }}</option>
                </select-input>
            </search-filter>
        </div>

        <div class="d-flex justify-content-between">
            <results :meta="users.meta" />
            <div>
                <i class="bi bi-download"></i>
                {{ __('Export') }}
                <a :href="getExportUrl('xlsx')">
                    {{ __('XLSX') }}
                </a>
                /
                <a :href="getExportUrl('csv')">
                    {{ __('CSV') }}
                </a>
                /
                <a :href="getExportUrl('ods')">
                    {{ __('ODS') }}
                </a>
            </div>
        </div>

        <div class="card">
            <data-table :items="users.data"
                        :links="users.meta.links"
                        :columns="columns"
                        >
                <data-table-row v-for="user in users.data" :key="user.id">
                    <td>
                        {{ user.id }}
                    </td>
                    <td>
                        <avatar :user="user" class="" /> 
                    </td>
                    <td>
                        <div v-if="user.deleted_at">
                            {{ user.full_name || '-' }}
                        </div>
                        <inertia-link class="" :href="route('admin.users.edit', user)" v-else>
                            {{ user.full_name || '-' }}
                        </inertia-link>
                    </td>
                    <td>
                        {{ user.account.name }}
                    </td>
                    <td>
                        {{ user.role ? user.role.translated_name : '-' }}
                    </td>
                    <td>
                        <a :href="'mailto:' + user.email">
                            {{ user.email }}
                        </a>
                    </td>
                    <td>
                        <template v-if="user.last_login_at">
                            {{ user.last_login_at | localizeDateTime }}
                        </template>
                        <template v-else>-</template>
                    </td>
                    <td>
                        {{ user.notes || '-' }}
                    </td>
                    <td class="text-end py-0">
                        <template v-if="user.deleted_at">
                            <button type="button" class="btn btn-outline-success" @click="restore(user)">
                                {{ __('Restore') }}
                            </button>
                            <button type="button" class="btn btn-outline-danger" @click="destroyPermanently(user)">
                                {{ __('Destroy permanently') }}
                            </button>
                        </template>
                        <div class="dropdown" v-else>
                            <button class="btn btn-outline-secondary" 
                                    type="button" 
                                    :id="`dropdownMenuUser-${user.id}`" 
                                    :title="__('Edit')"
                                    data-bs-toggle="dropdown" 
                                    aria-expanded="false">
                                <i class="bi bi-three-dots-vertical"></i>
                                {{ __('Edit') }}
                            </button>
                            <div class="dropdown-menu dropdown-menu-end" :aria-labelledby="`dropdownMenuUser-${user.id}`">
                                <inertia-link :href="route('admin.users.edit', {user})" class="dropdown-item">
                                    {{ __('Bearbeiten') }}
                                </inertia-link>
                                <inertia-link
                                    :href="route('impersonate', user)" 
                                    class="dropdown-item"
                                    v-if="$page.props.auth.user.can.impersonate_users"
                                    >
                                    {{ __('Als Benutzer ausgeben') }}
                                </inertia-link>
                            </div>
                        </div>
                    </td>
                </data-table-row>
            </data-table>

        </div>

    </div>

</template>

<script>

    import DataTable from '@/shared/Admin/DataTable';
    import DataTableRow from '@/shared/Admin/DataTableRow';
    import SearchFilter from '@/shared/Admin/SearchFilter';

    import SelectInput from '@/shared/Inputs/SelectInput';
    import Results from '@/shared/Admin/Results';

    import Avatar from '@/shared/Avatar';

    export default {
        props: {
            filters: Object,
            users: Object,
            accounts: Array,
            roles: Array,
        },
        components: {
            DataTable,
            DataTableRow,
            SearchFilter,
            SelectInput,
            Results,
            Avatar,
        },
        data() {
            return {
                form: {
                    search: this.filters.search,
                    status: this.filters.status,
                    account_id: this.filters.account_id,
                    role_id: this.filters.role_id,
                },
                columns: {
                    id: {label: 'ID', sortable: 'id'},
                    avatar: {label: 'Avatar'},
                    name: {label: 'Name', sortable: 'last_name'},
                    account: {label: 'Account', sortable: 'account.name'},
                    role: {label: 'Role'},
                    email: {label: 'Email', sortable: 'email'},
                    last_login_at: {label: 'Last login'},
                    notes: {label: 'Notes'},
                },
            }
        },
        watch: {
            form: {
                deep: true,
                handler: _.debounce(function () {
                    this.$inertia.get(this.route('admin.users.index'), this.form, {preserveState: true})
                }, 300, {trailing: true}),
            },
        },
        methods: {
            reset() {
                this.form = _.mapValues(this.form, () => null)
            },
            destroyPermanently(user) {
                if (confirm(this.__('Sind Sie sicher?'))) {
                    this.$inertia.delete(this.route('admin.users.destroy-permanently', user.id));
                }
            },
            restore(user) {
                this.$inertia.get(this.route('admin.users.restore', user.id));
            },
            getExportUrl(format) {
                return this.route('admin.users.export', {format: format, ...this.filters});
            }
        },
    }
</script>